
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import './createJobForm.css'
import useFetchAllLocationsCompanies from '../../../hooks/company/useFetchAllLocationsCompanies';
import useUserInfo from '../../../hooks/useUserInfo';
import useCurrentRestaurant from '../../../hooks/useCurrentRestaurant';
import { createJob, updateJob } from '../../../api/job.service';

const CreateJobForm = ({ onJobCreated, isEditing }) => {
    const { id } = useParams();
    const {locations, isLoading, error } = useFetchAllLocationsCompanies();
    const { userInfo, loading: userInfoLoading } = useUserInfo();
    const { restaurantId } = useCurrentRestaurant();
    
    const initialFormData = {
      position: '',
      schedule: '',
      contract: '',
      vacancies: '',
      yearsOfExperience: '',
      description: '',
      requirements: '',
      locationId: '',
      restaurantId: '',
      salary: 0,
      propina: 'Si',
      functions: '',
      questions: [],
      startDate: '',
      endDate: '',
    };
    const [errors, setErrors] = useState({})
    const [state, setState] = useState(initialFormData);

    useEffect(() => {
      if (isEditing) {
        fetchJobData();
      }
    }, [id]);

    const fetchJobData = async () => {
      try {

        const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/jobs/${id}`, {
          method: 'GET',
          credentials: 'include',
        });

      

        if (!response.ok) {
          const errorData = await response.json();
          console.error('❌ Failed to fetch job data:', errorData);
          throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
        }

        const apiResponse = await response.json();
        
        // Extract the job data from the API response structure
        const jobData = apiResponse.data || apiResponse;
        
        // Ensure questions is always an array and fix field mapping
        // Handle special location: if location address is "Todas las sucursales", set locationId to -1
        let locationId = jobData.locationId;
        if (jobData.location && jobData.location.address === 'Todas las sucursales') {
          locationId = -1;
        } 
        
        if (!jobData.locationId && !jobData.location) {
          locationId = null; // No especificado
        }

        // Format dates for input fields (YYYY-MM-DD format)
        const formatDateForInput = (date) => {
          if (!date) return '';
          const d = new Date(date);
          return d.toISOString().split('T')[0];
        };

        const safeJobData = {
          ...jobData,
          questions: Array.isArray(jobData.questions) ? jobData.questions : [],
          propina: jobData.tips ? 'Si' : 'No',
          locationId: locationId,
          startDate: formatDateForInput(jobData.startDate),
          endDate: formatDateForInput(jobData.endDate)
        };
        
        setState(safeJobData);
      } catch (error) {
        console.error('❌ Error fetching job data:', error);
        alert(`Error loading job data: ${error.message}`);
      }
    };


    const validateFields = (state) => {
      const fields = [
        { name: 'position', message: 'La posición es obligatoria' },
        { name: 'schedule', message: 'El horario es obligatorio' },
        { name: 'contract', message: 'El contrato es obligatorio' },
        { name: 'vacancies', message: 'Las vacantes son obligatorias' },
        { name: 'functions', message: 'Las funciones son obligatorias' },
        { name: 'description', message: 'La descripción es obligatoria' },
        { name: 'requirements', message: 'Los requisitos son obligatorios' },
        { name: 'salary', message: 'El salario es obligatorio' },
        { name: 'period', message: 'El periodo es obligatorio' },
      ];

      const errors = {};

      fields.forEach((field) => {
        if (!state[field.name]) {
          errors[field.name] = field.message;
        }
      });

      // Location validation: allow null, -1, or positive integers
      if (state.locationId !== null && state.locationId !== -1 && state.locationId !== '' && (!state.locationId || state.locationId <= 0)) {
        errors.locationId = 'La ubicación es obligatoria';
      }

      if(state.yearsOfExperience === null || state.yearsOfExperience === undefined || state.yearsOfExperience === '') {
        errors.yearsOfExperience = 'Los años de experiencia son obligatorios';
      }

      if (!state.questions || state.questions.length === 0) {
        errors.questions = 'Tienes que agregar al menos 1 pregunta';
      }

      return errors;
    };


    const handleSubmit = async (e) => {
      e.preventDefault();

      const formData = { ...state };
      
      // Ensure restaurantId is set from context
      if (restaurantId) {
        formData.restaurantId = restaurantId;
      }

      const validationErrors = validateFields(formData);

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      try {
      
        let data;
        if (isEditing) {
          data = await updateJob(id, formData, restaurantId);
          onJobCreated('¡Trabajo actualizado con éxito!');
        } else {
          data = await createJob(formData, restaurantId);
          onJobCreated('¡Trabajo creado con éxito!');
        }
        
      } catch (error) {
        console.error('❌ Error submitting form:', error);
        alert(`Error: ${error.message}`);
      }
    };

    const handleInputChange = (field, value) => {
      setState((prevState) => ({ ...prevState, [field]: value }));
    };

    const addQuestion = () => {
      setState((prevState) => ({
        ...prevState,
        questions: [...prevState.questions, { question: '' }],
      }));
    };

    const updateQuestion = (index, value) => {
      const updatedQuestions = [...state.questions];
      updatedQuestions[index].question = value;
      setState((prevState) => ({ ...prevState, questions: updatedQuestions }));
    };

    const deleteQuestion = (index) => {
      setState((prevState) => {
        const updatedQuestions = prevState.questions.filter((_, i) => i !== index);
        return { ...prevState, questions: updatedQuestions };
      });
    };

    return (
      <form onSubmit={handleSubmit} className="create-job-form__container">
        <h2>{isEditing ? 'Edita este trabajo' : ''}</h2>
        <div className="create-job-form__inputs">
          <div className="create-job-form__group">
            <label htmlFor="location">Posición:</label>
            <select
              id="position"
              name="position"
              value={state.position}
              onChange={(e) => handleInputChange('position', e.target.value)}
              className="create-job-form__input"
            >
              <option value="">Posición</option>
              <option value="Garzón">Garzón</option>
              <option value="Runner">Runner</option>
              <option value="Chef">Chef</option>
              <option value="Ayudante de Cocina">Ayudante de Cocina</option>
              <option value="Anfitrión">Anfitrión</option>
              <option value="Delivery">Delivery</option>
              <option value="Cajero">Cajero</option>
              <option value="Copero">Copero</option>
              <option value="Barista">Barista</option>
              <option value="Bartender">Bartender</option>
              <option value="Sommelier">Sommelier</option>
              <option value="Maitre">Maitre</option>
              <option value="Jefe de salón">Jefe de salón</option>
              <option value="Limpieza">Limpieza</option>
              <option value="Chef Ejecutivo">Chef Ejecutivo</option>
              <option value="Sous Chef">Sous Chef</option>
              <option value="Jefe de Cocina">Jefe de Cocina</option>
              <option value="Maestro de Cocina">Maestro de Cocina</option>
              <option value="Maestro Pastelero">Maestro Pastelero</option>
              <option value="Pastelero">Pastelero</option>
              <option value="Panadero">Panadero</option>
              <option value="Repostero">Repostero</option>
              <option value="Charcutero">Charcutero</option>
              <option value="Pizzero">Pizzero</option>
              <option value="Itamae">Itamae</option>
              <option value="Sushiman">Sushiman</option>
              <option value="Ayudante de Sushi">Ayudante de Sushi</option>
              <option value="Parrillero">Parrillero</option>
              <option value="Cocinero Frío">Cocinero Frío</option>
              <option value="Cocinero Caliente">Cocinero Caliente</option>
              <option value="Manipulador de Alimentos">Manipulador de Alimentos</option>
              <option value="Encargado de Producción">Encargado de Producción</option>
              <option value="Operador de Cocina">Operador de Cocina</option>
              <option value="Operador de Planta">Operador de Planta</option>
              <option value="Operador Multifuncional">Operador Multifuncional</option>
              <option value="Encargado de Reservas">Encargado de Reservas</option>
              <option value="Recepcionista de Restaurante">Recepcionista de Restaurante</option>
              <option value="Supervisor de Salón">Supervisor de Salón</option>
              <option value="Personal de Banquetería">Personal de Banquetería</option>
              <option value="Encargado de Bodega">Encargado de Bodega</option>
              <option value="Repositor">Repositor</option>
              <option value="Personal de Mantenimiento">Personal de Mantenimiento</option>
              <option value="Jefe de Local">Jefe de Local</option>
              <option value="Administrador de Local">Administrador de Local</option>
              <option value="Jefe de Sucursales">Jefe de Sucursales</option>
              <option value="Administrador de Restaurante">Administrador de Restaurante</option>
              <option value="Encargado de Compras">Encargado de Compras</option>
              <option value="Control de Calidad">Control de Calidad</option>
              <option value="Catador de Vinos">Catador de Vinos</option>
              <option value="Coordinador de Banquetes">Coordinador de Banquetes</option>
              <option value="Montajista">Montajista</option>
              <option value="Mixólogo">Mixólogo</option>
            </select>
            {errors.position && <span className="create-job-form__error-message">{errors.position}</span>}
          </div>

          {/* Restaurant is now handled by the navbar toggle - no need for selection here */}

          <div className="create-job-form__group">
            <label htmlFor="schedule">Horario:</label>
            <select
              id="schedule"
              value={state.schedule}
              onChange={(e) => handleInputChange('schedule', e.target.value)}
              className="create-job-form__input"
            >
              <option value="">Selecciona un horario</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Otro">Otro</option>
            </select>
            {errors.schedule&& <span className="create-job-form__error-message">{errors.schedule}</span>}
          </div>

          <div className="create-job-form__group">
            <label htmlFor="salary">Salario:</label>
            <input
              type="text"
              id="salary"
              value={state.salary}
              onChange={(e) => handleInputChange('salary', e.target.value)}
              className="create-job-form__input"
            />
            {errors.salary && <span className="create-job-form__error-message">{errors.salary}</span>}
          </div>

          <div className="create-job-form__group">
            <label htmlFor="propina">Propinas:</label>
            <select
              id="propina"
              value={state.propina}
              onChange={(e) => handleInputChange('propina', e.target.value)}
              className="create-job-form__input"
            >
              <option value="Si">Si</option>
              <option value="No">No</option>
            </select>
          </div>

          <div className="create-job-form__group">
            <label htmlFor="contract">Contrato:</label>
            <select
              id="contract"
              value={state.contract}
              onChange={(e) => handleInputChange('contract', e.target.value)}
              className="create-job-form__input"
            >
              <option value="">Selecciona tipo de contrato</option>
              <option value="A Plazo">A Plazo</option>
              <option value="Indefinido">Indefinido</option>
              <option value="Honorarios">Honorarios</option>
              <option value="Práctica">Práctica</option>
              <option value="Otros">Otros</option>
            </select>
            {errors.contract && <span className="create-job-form__error-message">{errors.contract}</span>}
          </div>

          <div className="create-job-form__group">
            <label htmlFor="vacancies">Vacantes:</label>
            <input
              type="text"
              id="vacancies"
              value={state.vacancies}
              onChange={(e) => handleInputChange('vacancies', e.target.value)}
              className="create-job-form__input"
            />
            {errors.vacancies && <span className="create-job-form__error-message">{errors.vacancies}</span>}
          </div>

          <div className="create-job-form__group">
            <label htmlFor="yearsOfExperience">Años de experiencia:</label>
            <select
              id="yearsOfExperience"
              value={state.yearsOfExperience}
              onChange={(e) => handleInputChange('yearsOfExperience', e.target.value)}
              className="create-job-form__input"
            >
              <option value="">Selecciona años de experiencia</option>
              <option value="0">Sin experiencia</option>
              <option value="1">1 año</option>
              <option value="2">2 años</option>
              <option value="3">3 años</option>
              <option value="4">4 años</option>
              <option value="5">+ 5 años</option>
            </select>
            {errors.yearsOfExperience && <span className="create-job-form__error-message">{errors.yearsOfExperience}</span>}
          </div>

          <div className="create-job-form__group">
            <label htmlFor="period">Periodo:</label>
            <select
              id="period"
              value={state.period}
              onChange={(e) => handleInputChange('period', e.target.value)}
              className="create-job-form__input"
            >
              <option value="">Selecciona un periodo</option>
              <option value="Permanente">Permanente</option>
              <option value="Reemplazo Temporal">Reemplazo Temporal</option>
              <option value="Reemplazo Urgente">Reemplazo Urgente</option>
              <option value="Sin información">Sin información</option>
            </select>
            {errors.period && <span className="create-job-form__error-message">{errors.period}</span>}
          </div>

          <div className="create-job-form__group">
            <label htmlFor="startDate">Fecha de inicio:</label>
            <input
              type="date"
              id="startDate"
              value={state.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              className="create-job-form__input"
            />
            {errors.startDate && <span className="create-job-form__error-message">{errors.startDate}</span>}
          </div>

          <div className="create-job-form__group">
            <label htmlFor="endDate">Fecha de fin (opcional):</label>
            <input
              type="date"
              id="endDate"
              value={state.endDate}
              onChange={(e) => handleInputChange('endDate', e.target.value)}
              className="create-job-form__input"
            />
            <small className="create-job-form__help-text">Dejar vacío para trabajo permanente</small>
            {errors.endDate && <span className="create-job-form__error-message">{errors.endDate}</span>}
          </div>

          {/* Location */}
          <div className="create-job-form__group">
            <label htmlFor="location">Ubicación:</label>
            <select
              id="location"
              value={state.locationId === null ? 'null' : state.locationId === -1 ? '-1' : state.locationId || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'null') {
                  handleInputChange('locationId', null);
                } else if (value === '-1') {
                  handleInputChange('locationId', -1);
                } else if (value === '') {
                  handleInputChange('locationId', '');
                } else {
                  handleInputChange('locationId', parseInt(value, 10));
                }
              }}
              className="create-job-form__input"
            >
              <option value="">Selecciona una ubicación</option>
              <option value="-1">Todas las sucursales</option>
              <option value="null">No especificado</option>
              {(() => {
                
                if (Array.isArray(locations)) {
                  return locations.map((loc) => {
                    
                    return (
                      <option key={loc.locationId} value={loc.locationId}>
                        {loc.address}
                      </option>
                    );
                  });
                }
                return null;
              })()}
            </select>
            {errors.locationId && (
              <span className="create-job-form__error-message">{errors.locationId}</span>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="create-job-form__description-container">
          <label htmlFor="description">Descripción:</label>
          <textarea
            type="text"
            id="job-description"
            value={state.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="create-job-form__input"
          >
          </textarea>
          {errors.description && <span className="create-job-form__error-message">{errors.description}</span>}
        </div>

        <div className="create-job-form__description-container">
          <label htmlFor="functions">Funciones:</label>
          <textarea
            type="text"
            id="job-description"
            value={state.functions}
            onChange={(e) => handleInputChange('functions', e.target.value)}
            className="create-job-form__input"
          >
          </textarea>
          {errors.functions && <span className="create-job-form__error-message">{errors.functions}</span>}
        </div>

        <div className="create-job-form__description-container">
          <label htmlFor="requirements">Requisitos:</label>
          <textarea
            type="text"
            id="job-description"
            value={state.requirements}
            className="create-job-form__input"
            onChange={(e) => handleInputChange('requirements', e.target.value)}
          >

          </textarea>
          {errors.requirements && <span className="create-job-form__error-message">{errors.requirements}</span>}
        </div>

        <div className="create-job-form__title-questions">
          <h2>Preguntas</h2>
          <button type="button" className="create-job-form__add-question-button" onClick={addQuestion}>
            Agregar pregunta
          </button>
        </div>

        <div>
          {Array.isArray(state.questions) && state.questions.map((quest, index) => (
            <div key={index} className="create-job-form__questions-container">
              <h3>Pregunta {index + 1}</h3>
                  <button
                    type="button"
                    className="create-job-form__delete-question-button"
                    onClick={() => deleteQuestion(index)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                  <div className="create-job-form__group">
                    <label>Enunciado:</label>
                    <input
                      type="text"
                      value={quest.question}
                      className="create-job-form__input"
                      onChange={(e) => updateQuestion(index, e.target.value)}
                    />
                  </div>
            </div>
          ))}
          {errors.questions && <span className="create-job-form__error-message">{errors.questions}</span>}
        </div>
        <div className="create-job-form__submit-button-container">
          <button type="submit" className="create-job-form__submit-button">
            {isEditing ? 'Editar' : 'Crear'}
          </button>
        </div>
      </form>
    );
};

export default CreateJobForm;







